import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export interface Review {
  id: string;
  propertyId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  rating: number;
  title: string;
  comment: string;
  timestamp: Date;
  likes: number;
  dislikes: number;
  verified: boolean;
  photos?: string[];
  parentReviewId?: string | null; // For replies - null means it's a top-level review
  landlordResponse?: {
    comment: string;
    timestamp: Date;
  };
}

export interface PropertyRating {
  propertyId: string;
  averageRating: number;
  totalReviews: number;
  ratingBreakdown: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
}

export interface ReviewComment {
  id: string;
  reviewId: string;
  userId: string;
  userName: string;
  content: string;
  timestamp: Date;
  parentId?: string; // for replies
}

interface ReviewContextType {
  reviews: Review[];
  propertyRatings: PropertyRating[];
  loading: boolean;
  loadReviews: (propertyId?: string) => Promise<void>;
  addReview: (review: Omit<Review, 'id' | 'timestamp' | 'likes' | 'dislikes'>) => void;
  updateReview: (review: Review) => void;
  removeReview: (reviewId: string) => void;
  getPropertyReviews: (propertyId: string) => Review[];
  getPropertyRating: (propertyId: string) => PropertyRating | null;
  likeReview: (reviewId: string) => void;
  dislikeReview: (reviewId: string) => void;
  addComment: (comment: Omit<ReviewComment, 'id' | 'timestamp'>) => void;
  getComments: (reviewId: string) => ReviewComment[];
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export const ReviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [propertyRatings, setPropertyRatings] = useState<PropertyRating[]>([]);
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [loading, setLoading] = useState(false);

  // Load reviews from backend
  const loadReviews = useCallback(async (propertyId?: string) => {
    try {
      setLoading(true);
      
      let response;
      
      if (propertyId) {
        response = await api.get(`/reviews/property/${propertyId}`);
      } else {
        // Load all reviews for the current user
        response = await api.get('/reviews/user', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }
      
      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          const formattedReviews = data.reviews.map((review: any) => ({
            id: review.id,
            propertyId: review.propertyId,
            userId: review.userId,
            userName: review.User ? `${review.User.firstName} ${review.User.lastName}` : 'Unknown User',
            userAvatar: review.User?.avatar,
            rating: review.rating,
            title: review.comment.substring(0, 50) + (review.comment.length > 50 ? '...' : ''),
            comment: review.comment,
            timestamp: new Date(review.createdAt),
            likes: 0,
            dislikes: 0,
            verified: false,
            photos: review.images || []
          }));
          
          if (propertyId) {
            // For specific property, merge with existing reviews
            setReviews(prev => {
              const existingReviews = prev.filter(r => r.propertyId !== propertyId);
              return [...existingReviews, ...formattedReviews];
            });
          } else {
            setReviews(formattedReviews);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the signed-in user's own reviews on mount. Anonymous visitors have
  // no "my reviews" to fetch — calling this unauthenticated sent
  // Authorization: Bearer null on every single page load (this context wraps
  // the whole app), which 401'd and cascaded into the axios interceptor's
  // silent-refresh-then-redirect logic, hard-redirecting guests browsing
  // public pages (e.g. a shared property link) straight to /login.
  useEffect(() => {
    if (user) {
      loadReviews();
    }
  }, [loadReviews, user]);

  // Calculate property ratings whenever reviews change
  useEffect(() => {
    const calculateRatings = () => {
      const ratingsMap = new Map<string, PropertyRating>();

      // Only count top-level reviews (exclude replies) for rating calculation
      // Replies have parentReviewId set, so we filter those out
      const topLevelReviews = reviews.filter(review => !review.parentReviewId);

      topLevelReviews.forEach(review => {
        const existing = ratingsMap.get(review.propertyId);
        if (existing) {
          existing.totalReviews++;
          existing.ratingBreakdown[review.rating as keyof typeof existing.ratingBreakdown]++;
        } else {
          ratingsMap.set(review.propertyId, {
            propertyId: review.propertyId,
            averageRating: 0,
            totalReviews: 1,
            ratingBreakdown: {
              5: review.rating === 5 ? 1 : 0,
              4: review.rating === 4 ? 1 : 0,
              3: review.rating === 3 ? 1 : 0,
              2: review.rating === 2 ? 1 : 0,
              1: review.rating === 1 ? 1 : 0,
            }
          });
        }
      });

      // Calculate average ratings
      ratingsMap.forEach(rating => {
        const total = Object.entries(rating.ratingBreakdown)
          .reduce((sum, [stars, count]) => sum + (parseInt(stars) * count), 0);
        rating.averageRating = total / rating.totalReviews;
      });

      const newRatings = Array.from(ratingsMap.values());
      setPropertyRatings(newRatings);
    };

    calculateRatings();
  }, [reviews]);

  const addReview = useCallback(async (reviewData: Omit<Review, 'id' | 'timestamp' | 'likes' | 'dislikes'>) => {
    try {
      const response = await api.post('/reviews', {
        propertyId: reviewData.propertyId,
        rating: reviewData.rating,
        comment: reviewData.comment
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          const newReview: Review = {
            ...reviewData,
            id: data.review.id,
            timestamp: new Date(data.review.createdAt),
            likes: 0,
            dislikes: 0,
          };
          setReviews(prev => [newReview, ...prev]);
        }
      }
    } catch (error) {
      console.error('Error adding review:', error);
    }
  }, []);

  const updateReview = useCallback(async (updated: Review) => {
    try {
      const response = await api.put(`/reviews/${updated.id}`, {
        rating: updated.rating,
        comment: updated.comment
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          setReviews(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated, timestamp: new Date() } : r));
        }
      }
    } catch (error) {
      console.error('Error updating review:', error);
    }
  }, []);

  const removeReview = useCallback(async (reviewId: string) => {
    try {
      const response = await api.delete(`/reviews/${reviewId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 200) {
        setReviews(prev => prev.filter(r => r.id !== reviewId));
      }
    } catch (error) {
      console.error('Error removing review:', error);
    }
  }, []);

  const getPropertyReviews = useCallback((propertyId: string) => {
    return reviews.filter(review => review.propertyId === propertyId)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [reviews]);

  const getPropertyRating = useCallback((propertyId: string) => {
    return propertyRatings.find(rating => rating.propertyId === propertyId) || null;
  }, [propertyRatings]);

  const likeReview = useCallback((reviewId: string) => {
    setReviews(prev =>
      prev.map(review =>
        review.id === reviewId
          ? { ...review, likes: review.likes + 1 }
          : review
      )
    );
  }, []);

  const dislikeReview = useCallback((reviewId: string) => {
    setReviews(prev =>
      prev.map(review =>
        review.id === reviewId
          ? { ...review, dislikes: review.dislikes + 1 }
          : review
      )
    );
  }, []);

  const addComment = useCallback((commentData: Omit<ReviewComment, 'id' | 'timestamp'>) => {
    const newComment: ReviewComment = {
      ...commentData,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
    };
    setComments(prev => [newComment, ...prev]);
  }, []);

  const getComments = useCallback((reviewId: string) => {
    return comments.filter(c => c.reviewId === reviewId).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [comments]);

  const value = useMemo(() => ({
    reviews,
    propertyRatings,
    loading,
    loadReviews,
    addReview,
    updateReview,
    removeReview,
    getPropertyReviews,
    getPropertyRating,
    likeReview,
    dislikeReview,
    addComment,
    getComments
  }), [
    reviews, propertyRatings, loading, loadReviews, addReview, updateReview,
    removeReview, getPropertyReviews, getPropertyRating, likeReview,
    dislikeReview, addComment, getComments,
  ]);

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
};

export const useReviews = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReviews must be used within ReviewProvider');
  }
  return context;
}; 